// Utility functions for player name display management
// File: src/utils/nameUtils.js

/**
 * Generate display names for players to avoid conflicts
 * Returns an array of players with displayName property added
 */
export const generateDisplayNames = (players) => {
  if (!players || !Array.isArray(players)) {
    console.warn('generateDisplayNames: Invalid players array provided');
    return [];
  }

  // Create a copy to avoid mutation and handle null values
  const validPlayers = players.filter(player => player && player.id);
  
  const firstNameCounts = {};
  
  // Count occurrences of each first name
  validPlayers.forEach(player => {
    const firstName = player.firstName || player.name?.split(' ')[0] || '';
    firstNameCounts[firstName] = (firstNameCounts[firstName] || 0) + 1;
  });

  // Add displayName property to each player
  return validPlayers.map(player => {
    const firstName = player.firstName || player.name?.split(' ')[0] || '';
    const lastName = player.lastName || player.name?.split(' ').slice(1).join(' ') || '';
    
    let displayName;
    if (firstNameCounts[firstName] > 1 && lastName) {
      displayName = `${firstName} ${lastName.charAt(0)}`;
    } else {
      displayName = firstName || 'Player';
    }

    return {
      ...player,
      displayName
    };
  });
};

/**
 * Get display name for a single player given all players context
 */
export const getPlayerDisplayName = (player, allPlayers) => {
  if (!player) return 'Unknown Player';
  
  if (!allPlayers || !Array.isArray(allPlayers)) {
    return player.displayName || player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'Player';
  }
  
  try {
    const playersWithDisplayNames = generateDisplayNames(allPlayers);
    const playerWithDisplayName = playersWithDisplayNames.find(p => p && p.id === player.id);
    return playerWithDisplayName ? 
      (playerWithDisplayName.displayName || playerWithDisplayName.name) : 
      (player.displayName || player.name || 'Player');
  } catch (error) {
    console.warn('Error getting player display name:', error);
    return player.displayName || player.name || 'Player';
  }
};

/**
 * Legacy function for backward compatibility - returns name mapping object
 * Keep this if other parts of your app use the old format
 */
export const generateDisplayNamesMap = (players) => {
  const nameMap = {};
  const firstNameCounts = {};
  
  if (!players || !Array.isArray(players)) {
    return nameMap;
  }
  
  players.forEach(player => {
    if (player && player.name) {
      const firstName = player.firstName || player.name?.split(' ')[0] || '';
      firstNameCounts[firstName] = (firstNameCounts[firstName] || 0) + 1;
    }
  });

  players.forEach(player => {
    if (player && player.name) {
      const firstName = player.firstName || player.name?.split(' ')[0] || '';
      const lastName = player.lastName || player.name?.split(' ')[1] || '';
      
      if (firstNameCounts[firstName] > 1) {
        nameMap[player.name] = `${firstName} ${lastName.charAt(0)}`;
      } else {
        nameMap[player.name] = firstName;
      }
    }
  });

  return nameMap;
};

/**
 * Format partnership display
 */
export const formatPartnership = (player1, player2) => {
  if (!player1 || !player2) return 'Unknown Partnership';
  
  const name1 = player1.displayName || player1.name || 'Player 1';
  const name2 = player2.displayName || player2.name || 'Player 2';
  
  return `${name1} & ${name2}`;
};

/**
 * Format team vs team display
 */
export const formatTeamVs = (team1, team2) => {
  if (!team1 || !Array.isArray(team1) || team1.length < 2 ||
      !team2 || !Array.isArray(team2) || team2.length < 2) {
    return 'Unknown Matchup';
  }
  
  const team1Display = formatPartnership(team1[0], team1[1]);
  const team2Display = formatPartnership(team2[0], team2[1]);
  
  return `${team1Display} vs ${team2Display}`;
};